import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  afterNextRender,
  inject,
  viewChild,
  DestroyRef,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";

/**
 * A rotating low-poly gem rendered with Three.js - the "island of gems"
 * signature moment. Three is dynamically imported so it never lands in the
 * SSR/prerender bundle and is code-split from the main chunk. Client-only,
 * cleans up its WebGL context on destroy, and idles gracefully if WebGL
 * is unavailable.
 */
@Component({
  selector: "app-gem",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<canvas
    #canvas
    class="block w-full h-full"
    aria-hidden="true"
  ></canvas>`,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class Gem {
  private platformId = inject(PLATFORM_ID);
  private destroyRef = inject(DestroyRef);
  private canvasRef =
    viewChild.required<ElementRef<HTMLCanvasElement>>("canvas");

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.init().catch(() => {
        /* WebGL unsupported or import failed - canvas stays empty, no crash */
      });
    });
  }

  private async init() {
    const canvas = this.canvasRef().nativeElement;
    const THREE = await import("three");

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    camera.position.set(0, 0, 6.4);

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const geometry = new THREE.IcosahedronGeometry(1.7, 1);
    const material = new THREE.MeshPhysicalMaterial({
      color: 0xf2c98a,
      metalness: 0.15,
      roughness: 0.08,
      clearcoat: 1,
      clearcoatRoughness: 0.05,
      reflectivity: 1,
      ior: 2.3,
      flatShading: true,
      emissive: 0x2a0f08,
      emissiveIntensity: 0.15,
    });
    const gem = new THREE.Mesh(geometry, material);
    scene.add(gem);

    const wireGeo = new THREE.IcosahedronGeometry(1.74, 1);
    const wireMat = new THREE.MeshBasicMaterial({
      color: 0xe8a23d,
      wireframe: true,
      transparent: true,
      opacity: 0.18,
    });
    gem.add(new THREE.Mesh(wireGeo, wireMat));

    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);

    const saffronLight = new THREE.PointLight(0xe8a23d, 30, 20);
    saffronLight.position.set(3, 2, 4);
    scene.add(saffronLight);

    const vermilionLight = new THREE.PointLight(0xc23b22, 25, 20);
    vermilionLight.position.set(-3, -1, 3);
    scene.add(vermilionLight);

    const tealLight = new THREE.PointLight(0x1f6e63, 20, 20);
    tealLight.position.set(0, -3, -2);
    scene.add(tealLight);

    const el = canvas.parentElement ?? canvas;
    let raf = 0;
    let destroyed = false;
    let pointerX = 0;
    let pointerY = 0;

    const resize = () => {
      const w = el.clientWidth || 1;
      const h = el.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(el);

    const onPointerMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      pointerX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      pointerY = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    if (!reduced) {
      window.addEventListener("pointermove", onPointerMove, { passive: true });
    }

    let t = 0;
    const tick = () => {
      if (destroyed) return;
      t += 0.0062;
      gem.rotation.y = t * (reduced ? 0.3 : 1) + pointerX * 0.3;
      gem.rotation.x = Math.sin(t * 0.6) * 0.15 + pointerY * 0.2;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    this.destroyRef.onDestroy(() => {
      destroyed = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onPointerMove);
      geometry.dispose();
      wireGeo.dispose();
      material.dispose();
      wireMat.dispose();
      renderer.dispose();
    });
  }
}
